import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";

import { Role } from "../../../api";
import { getAllMenuKeys } from "../../../utils/permissions";
import { ACTIONS } from "../../../utils/constants";
import { usePermissions } from "../../../hooks";

import styles from "./Roles.module.scss";

const LIMIT_OPTIONS = [10, 20, 50];

function formatDate(date) {
  if (!date) {
    return "—";
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("es-GT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsedDate);
}

function getVisiblePages(currentPage, totalPages) {
  const pages = [];
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, currentPage + 2);

  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }

  return pages;
}

function getPaginationResponse(result, page, limit) {
  const response = result?.roles || result?.response || result || {};

  const docs = Array.isArray(response.docs)
    ? response.docs
    : Array.isArray(response)
      ? response
      : [];

  const totalDocs =
    Number(response.totalDocs) ||
    Number(response.total) ||
    Number(response.totalItems) ||
    docs.length;

  const totalPages =
    Number(response.totalPages) ||
    Number(response.pages) ||
    Math.max(1, Math.ceil(totalDocs / limit));

  return {
    docs,
    page: Number(response.page) || page,
    limit: Number(response.limit) || limit,
    totalPages,
    totalDocs,
  };
}

function getPermissionCount(role) {
  const menuCount = Array.isArray(role?.menuPermissions)
    ? role.menuPermissions.length
    : 0;

  const actionCount = Array.isArray(role?.actionPermissions)
    ? role.actionPermissions.length
    : 0;

  return { menuCount, actionCount };
}

const TOTAL_MENUS = getAllMenuKeys().length;

export function RolesPage() {
  const { hasAction } = usePermissions();
  const canAdd = hasAction("roles", ACTIONS.ADD);
  const canEdit = hasAction("roles", ACTIONS.MOD);
  const canDelete = hasAction("roles", ACTIONS.DEL);

  const location = useLocation();
  const actionMenuRef = useRef(null);
  const actionMenuPanelRef = useRef(null);

  const [roles, setRoles] = useState([]);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalDocs: 0,
  });

  const [searchValue, setSearchValue] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    location.state?.success || ""
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadRoles = useCallback(
    async (page = 1, limit = pagination.limit, search = searchValue) => {
      try {
        setIsLoading(true);
        setError("");
        setOpenActionMenu(null);

        const roleCtrl = new Role();

        const filters = {};

        if (search.trim()) {
          filters.search = search.trim();
        }

        const result = await roleCtrl.getRoles(page, limit, filters);
        const response = getPaginationResponse(result, page, limit);

        setRoles(response.docs);

        setPagination({
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
          totalDocs: response.totalDocs,
        });
      } catch (errorResponse) {
        console.error("loadRoles error:", errorResponse);

        setRoles([]);

        setPagination({
          page: 1,
          limit: pagination.limit,
          totalPages: 1,
          totalDocs: 0,
        });

        setError(
          errorResponse?.msg ||
            "No fue posible cargar los roles. Intenta nuevamente."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [pagination.limit, searchValue]
  );

  useEffect(() => {
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setSuccessMessage("");
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [successMessage]);

  useEffect(() => {
    function handlePointerDown(event) {
      const clickedTrigger = actionMenuRef.current?.contains(event.target);
      const clickedPanel = actionMenuPanelRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedPanel) {
        setOpenActionMenu(null);
        setActionMenuPosition(null);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpenActionMenu(null);
        setActionMenuPosition(null);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (openActionMenu === null) {
      return undefined;
    }

    function handleReposition() {
      setOpenActionMenu(null);
      setActionMenuPosition(null);
    }

    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [openActionMenu]);

  function handleSearchSubmit(event) {
    event.preventDefault();

    loadRoles(1, pagination.limit, searchValue);
  }

  function handleClearSearch() {
    setSearchValue("");
    loadRoles(1, pagination.limit, "");
  }

  function handlePageChange(page) {
    if (page < 1 || page > pagination.totalPages || page === pagination.page) {
      return;
    }

    loadRoles(page);
  }

  function handleLimitChange(event) {
    const nextLimit = Number(event.target.value);

    loadRoles(1, nextLimit);
  }

  function handleOpenDeleteModal(role) {
    setOpenActionMenu(null);
    setActionMenuPosition(null);
    setRoleToDelete(role);
    setError("");
  }

  function handleCloseDeleteModal() {
    if (isDeleting) {
      return;
    }

    setRoleToDelete(null);
  }

  async function handleConfirmDelete() {
    if (!roleToDelete) {
      return;
    }

    try {
      setIsDeleting(true);
      setError("");

      const roleCtrl = new Role();

      await roleCtrl.deleteRole(roleToDelete._id);

      setSuccessMessage("Rol eliminado correctamente.");
      setRoleToDelete(null);

      const nextPage =
        roles.length === 1 && pagination.page > 1
          ? pagination.page - 1
          : pagination.page;

      await loadRoles(nextPage);
    } catch (errorResponse) {
      console.error("handleConfirmDelete error:", errorResponse);

      setError(
        errorResponse?.msg ||
          "No fue posible eliminar el rol. Intenta nuevamente."
      );
    } finally {
      setIsDeleting(false);
    }
  }

  const visiblePages = getVisiblePages(pagination.page, pagination.totalPages);

  const firstRoleNumber =
    pagination.totalDocs === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;

  const lastRoleNumber = Math.min(
    pagination.page * pagination.limit,
    pagination.totalDocs
  );

  return (
    <section className={styles.RolesPage}>
      <div className={styles.TopBar}>
        <form className={styles.SearchForm} onSubmit={handleSearchSubmit}>
          <input
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Buscar por código o nombre..."
            aria-label="Buscar roles"
          />

          <button type="submit" aria-label="Buscar roles">
            ⌕
          </button>
        </form>

        <div className={styles.TopActions}>
          {canAdd && (
            <Link to="/admin/roles/create" className={styles.CreateButton}>
              <span>＋</span>
              Nuevo rol
            </Link>
          )}
        </div>
      </div>

      {searchValue.trim() && (
        <div className={styles.Filters}>
          <span className={styles.ActiveSearch}>
            Búsqueda: <strong>{searchValue.trim()}</strong>
          </span>

          <button
            type="button"
            className={styles.ClearButton}
            onClick={handleClearSearch}
          >
            Limpiar búsqueda
          </button>
        </div>
      )}

      {successMessage && (
        <div className={styles.SuccessMessage} role="status">
          {successMessage}
        </div>
      )}

      {error && (
        <div className={styles.ErrorMessage} role="alert">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className={styles.StateMessage}>Cargando roles...</div>
      ) : roles.length === 0 ? (
        <div className={styles.EmptyState}>
          <p>No hay roles para mostrar.</p>
        </div>
      ) : (
        <>
          <p className={styles.TableInfo}>
            Mostrando {firstRoleNumber} a {lastRoleNumber} de{" "}
            {pagination.totalDocs} roles
          </p>

          <div className={styles.TableContainer}>
            <table className={styles.Table}>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Permisos</th>
                  <th>Fecha creación</th>
                  <th className={styles.ActionsColumn}>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {roles.map((role) => {
                  const { menuCount, actionCount } = getPermissionCount(role);

                  return (
                    <tr key={role._id}>
                      <td>
                        <span className={styles.RoleCode} title={role.id}>
                          {role.id}
                        </span>
                      </td>

                      <td>
                        <p className={styles.RoleName} title={role.name}>
                          {role.name}
                        </p>
                      </td>

                      <td>
                        <p
                          className={styles.Description}
                          title={role.description || "Sin descripción"}
                        >
                          {role.description || "Sin descripción"}
                        </p>
                      </td>

                      <td>
                        <span className={styles.PermissionBadge}>
                          {menuCount}/{TOTAL_MENUS} menús · {actionCount}{" "}
                          acciones
                        </span>
                      </td>

                      <td className={styles.DateCell}>
                        {formatDate(role.createdate)}
                      </td>

                      <td className={styles.ActionsColumn}>
                        {(canEdit || canDelete) && (
                        <div
                          ref={
                            openActionMenu === role._id ? actionMenuRef : null
                          }
                          className={styles.RowActions}
                        >
                          <button
                            type="button"
                            className={styles.MenuButton}
                            aria-label={`Acciones para ${role.name}`}
                            aria-expanded={openActionMenu === role._id}
                            aria-haspopup="menu"
                            onClick={(event) => {
                              if (openActionMenu === role._id) {
                                setOpenActionMenu(null);
                                setActionMenuPosition(null);
                                return;
                              }

                              const rect =
                                event.currentTarget.getBoundingClientRect();
                              const menuWidth = 210;
                              const viewportRight =
                                document.documentElement.clientWidth;
                              const left = Math.min(
                                rect.right + window.scrollX - menuWidth,
                                viewportRight + window.scrollX - menuWidth - 8
                              );

                              setActionMenuPosition({
                                top: rect.bottom + window.scrollY + 6,
                                left: Math.max(left, 8),
                              });
                              setOpenActionMenu(role._id);
                            }}
                          >
                            ⋮
                          </button>

                          {openActionMenu === role._id &&
                            actionMenuPosition &&
                            createPortal(
                              <div
                                ref={actionMenuPanelRef}
                                className={styles.ActionMenu}
                                style={{
                                  position: "absolute",
                                  top: actionMenuPosition.top,
                                  left: actionMenuPosition.left,
                                }}
                                role="menu"
                              >
                                {canEdit && (
                                <Link
                                  to={`/admin/roles/${role._id}/edit`}
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenActionMenu(null);
                                    setActionMenuPosition(null);
                                  }}
                                >
                                  Editar rol
                                </Link>
                                )}

                                {canDelete && (
                                <button
                                  type="button"
                                  className={styles.DeleteAction}
                                  role="menuitem"
                                  onClick={() => handleOpenDeleteModal(role)}
                                >
                                  Eliminar rol
                                </button>
                                )}
                              </div>,
                              document.body
                            )}
                        </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <footer className={styles.Pagination}>
            <label className={styles.LimitControl}>
              <span>Mostrar:</span>

              <select value={pagination.limit} onChange={handleLimitChange}>
                {LIMIT_OPTIONS.map((limit) => (
                  <option key={limit} value={limit}>
                    {limit}
                  </option>
                ))}
              </select>
            </label>

            <div className={styles.PaginationButtons}>
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                disabled={pagination.page <= 1}
                aria-label="Ir a la primera página"
              >
                «
              </button>

              <button
                type="button"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                aria-label="Ir a la página anterior"
              >
                ‹
              </button>

              {visiblePages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => handlePageChange(page)}
                  className={page === pagination.page ? styles.CurrentPage : ""}
                  aria-label={`Ir a la página ${page}`}
                  aria-current={page === pagination.page ? "page" : undefined}
                >
                  {page}
                </button>
              ))}

              <button
                type="button"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                aria-label="Ir a la página siguiente"
              >
                ›
              </button>

              <button
                type="button"
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.page >= pagination.totalPages}
                aria-label="Ir a la última página"
              >
                »
              </button>
            </div>
          </footer>
        </>
      )}

      {roleToDelete && (
        <div className={styles.DeleteModalOverlay}>
          <div
            className={styles.DeleteModal}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-role-title"
            aria-describedby="delete-role-description"
          >
            <div className={styles.DeleteModalIcon}>!</div>

            <h2 id="delete-role-title">Eliminar rol</h2>

            <p id="delete-role-description">
              ¿Deseas eliminar el rol <strong>{roleToDelete.name}</strong>?
            </p>

            <p className={styles.DeleteModalWarning}>
              No podrás eliminar un rol si todavía hay usuarios con este rol
              asignado.
            </p>

            <div className={styles.DeleteModalActions}>
              <button
                type="button"
                className={styles.CancelDeleteButton}
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                autoFocus
              >
                Cancelar
              </button>

              <button
                type="button"
                className={styles.ConfirmDeleteButton}
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Eliminando..." : "Eliminar rol"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
